// Declarative action binding for data-on* attributes.
// This replaces inline HTML event handlers while preserving behavior.
// Uses window function lookup instead of new Function()/eval to comply with strict CSP.

const compiledExpressionCache = new Map();
const elementBindingRegistry = new WeakMap();
const DATA_EVENT_PREFIX = 'data-on';

/**
 * Parse simple literal arguments for a function call expression.
 * Handles: event, this, true, false, null, undefined, numbers, quoted strings.
 */
function parseArgs(argsStr, event, element) {
    if (!argsStr.trim()) return [];
    return argsStr.split(',').map(arg => {
        const t = arg.trim();
        if (t === 'event') return event;
        if (t === 'this') return element;
        if (t === 'true') return true;
        if (t === 'false') return false;
        if (t === 'null') return null;
        if (t === 'undefined') return undefined;
        if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
        if ((t.startsWith("'") && t.endsWith("'")) || (t.startsWith('"') && t.endsWith('"'))) {
            return t.slice(1, -1);
        }
        return t;
    });
}

/**
 * Compile a declarative expression into a callable without using new Function()/eval.
 * Only supports simple function calls: functionName() or functionName(arg1, arg2, ...).
 * The function is looked up on window at call time, so late-bound globals are supported.
 */
function compileExpression(expression) {
    if (compiledExpressionCache.has(expression)) {
        return compiledExpressionCache.get(expression);
    }

    const trimmed = expression.trim();

    // Match: functionName(optionalArgs)
    const match = trimmed.match(/^([A-Za-z_$][A-Za-z0-9_$]*)\(([\s\S]*)\)$/);
    if (match) {
        const funcName = match[1];
        const rawArgs = match[2].trim();

        const fn = function(event, element) {
            const func = window[funcName];
            if (typeof func !== 'function') {
                console.warn(`Declarative action: "${funcName}" is not a function on window`);
                return;
            }
            const args = rawArgs ? parseArgs(rawArgs, event, element) : [];
            return func.apply(element, args);
        };

        compiledExpressionCache.set(expression, fn);
        return fn;
    }

    console.warn(`Declarative action "${expression}" is not a supported expression. Wrap complex logic in a named window function.`);
    const noop = () => {};
    compiledExpressionCache.set(expression, noop);
    return noop;
}

function markBinding(element, eventName, expression) {
    let bindings = elementBindingRegistry.get(element);
    if (!bindings) {
        bindings = new Set();
        elementBindingRegistry.set(element, bindings);
    }

    const key = `${eventName}::${expression}`;
    if (bindings.has(key)) {
        return false;
    }

    bindings.add(key);
    return true;
}

function bindAttributeAction(element, eventName, expression) {
    if (!eventName || !expression) {
        return;
    }

    if (!markBinding(element, eventName, expression)) {
        return;
    }

    let compiled;
    try {
        compiled = compileExpression(expression);
    } catch (error) {
        console.error(`Failed to compile declarative action "${expression}"`, error);
        return;
    }

    element.addEventListener(eventName, (event) => {
        try {
            const result = compiled(event, element, window, document);
            if (result === false) {
                event.preventDefault();
            }
        } catch (error) {
            console.error(`Declarative action "${expression}" failed`, error);
        }
    });
}

function getElements(root) {
    if (!root) {
        return [];
    }

    if (root.nodeType === Node.ELEMENT_NODE || root === document) {
        const descendants = root.querySelectorAll ? Array.from(root.querySelectorAll('*')) : [];
        return [root, ...descendants];
    }

    return [];
}

/**
 * Bind all data-on* attributes under the provided root.
 * @param {Document|Element} root
 */
export function initDeclarativeActions(root = document) {
    const elements = getElements(root);

    elements.forEach((element) => {
        if (!element.attributes) {
            return;
        }

        Array.from(element.attributes).forEach((attribute) => {
            if (!attribute.name.startsWith(DATA_EVENT_PREFIX)) {
                return;
            }

            const eventName = attribute.name.slice(DATA_EVENT_PREFIX.length).toLowerCase();
            const expression = (attribute.value || '').trim();
            bindAttributeAction(element, eventName, expression);
        });
    });
}
